'use client';

import { LENS_INDEX_COMPONENT, LENS_VIEW_AREA_COMPONENT } from '@/app/_lens-components/constants';
import {
  useApplyDefaultSelection,
  type ProgressiveViewAreaState,
} from '@/app/_lens-components/hooks/useInitDefaultSelection';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import { ELensViewAreaType } from '@/app/_lens-components/types';
import type { Currency, User } from '@/api/types/public';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';

export interface UseDefaultSelectionOnEnterParams {
  hasDefaultSelection: boolean;
  progressiveViewArea: ProgressiveViewAreaState | null;
  selectedLensViewAreaType: ELensViewAreaType | null;
  setSelectedLensViewAreaType: Dispatch<SetStateAction<ELensViewAreaType | null>>;
  currency: Currency | null;
  userInfo: User;
}

/**
 * 进入折射率 / 视野升级步骤时补默认勾选（小语种）。
 */
export function useDefaultSelectionOnEnter({
  hasDefaultSelection,
  progressiveViewArea,
  selectedLensViewAreaType,
  setSelectedLensViewAreaType,
  currency,
  userInfo,
}: UseDefaultSelectionOnEnterParams) {
  const { initDefaultLensIndexSelection, initDefaultLensViewAreaSelection } =
    useApplyDefaultSelection();
  const nowComponent = useLensProcessStore((s) => s.nowComponent);
  const selectedColorItem = useLensProcessStore((s) => s.selectedColorItem);
  const selectedLensIndexItem = useLensProcessStore((s) => s.selectedLensIndexItem);

  /** 进入折射率：优先恢复 localStorage 上次选中，否则推荐/首项 */
  useEffect(() => {
    if (!hasDefaultSelection || nowComponent !== LENS_INDEX_COMPONENT) return;
    if (!selectedColorItem?.lens_list?.length) return;
    initDefaultLensIndexSelection(selectedColorItem);
  }, [hasDefaultSelection, nowComponent, selectedColorItem, initDefaultLensIndexSelection]);

  /** 进入视野升级：无选中时按已保存偏好或默认策略补一次 */
  useEffect(() => {
    if (!hasDefaultSelection || nowComponent !== LENS_VIEW_AREA_COMPONENT) return;
    if (!progressiveViewArea || selectedLensViewAreaType != null) return;
    if (!currency || !selectedLensIndexItem || !selectedColorItem) return;
    initDefaultLensViewAreaSelection({
      progressiveViewArea,
      selectedLensViewAreaType,
      setSelectedLensViewAreaType,
      currency,
      userInfo,
    });
  }, [
    hasDefaultSelection,
    nowComponent,
    progressiveViewArea,
    selectedLensViewAreaType,
    selectedLensIndexItem,
    selectedColorItem,
    currency,
    userInfo,
    initDefaultLensViewAreaSelection,
    setSelectedLensViewAreaType,
  ]);
}
